import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';


const StudentProfileForm = ({
  student,
  onSave,
  onCancel,
  isEditing = false,
  isSaving = false,
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    grade: '',
    studentId: '',
    primaryDisability: '',
    secondaryDisabilities: [],
    accommodations: '',
    serviceProviders: [],
    emergencyContact: {
      name: '',
      relationship: '',
      phone: '',
      email: ''
    },
    medicalInfo: '',
    behavioralNotes: '',
    academicLevel: '',
    communicationMethod: '',
    transportationNeeds: '',
    dietaryRestrictions: '',
    iepStartDate: '',
    iepAnnualReviewDue: ''
  });

  const [errors, setErrors] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Form options
  const gradeOptions = [
    { value: 'pre-k', label: 'Pre-K' },
    { value: 'k', label: 'Kindergarten' },
    { value: '1', label: '1st Grade' },
    { value: '2', label: '2nd Grade' },
    { value: '3', label: '3rd Grade' },
    { value: '4', label: '4th Grade' },
    { value: '5', label: '5th Grade' },
    { value: '6', label: '6th Grade' },
    { value: '7', label: '7th Grade' },
    { value: '8', label: '8th Grade' },
    { value: '9', label: '9th Grade' },
    { value: '10', label: '10th Grade' },
    { value: '11', label: '11th Grade' },
    { value: '12', label: '12th Grade' }
  ];

  const disabilityOptions = [
    { value: 'autism', label: 'Autism Spectrum Disorder' },
    { value: 'intellectual', label: 'Intellectual Disability' },
    { value: 'learning', label: 'Specific Learning Disability' },
    { value: 'emotional', label: 'Emotional Behavioral Disorder' },
    { value: 'speech', label: 'Speech or Language Impairment' },
    { value: 'hearing', label: 'Hearing Impairment' },
    { value: 'visual', label: 'Visual Impairment' },
    { value: 'physical', label: 'Physical Disability' },
    { value: 'multiple', label: 'Multiple Disabilities' },
    { value: 'other', label: 'Other Health Impairment' }
  ];

  const serviceProviderOptions = [
    { value: 'speech-therapist', label: 'Speech-Language Pathologist' },
    { value: 'occupational-therapist', label: 'Occupational Therapist' },
    { value: 'physical-therapist', label: 'Physical Therapist' },
    { value: 'behavioral-specialist', label: 'Behavioral Specialist' },
    { value: 'social-worker', label: 'Social Worker' },
    { value: 'psychologist', label: 'School Psychologist' },
    { value: 'counselor', label: 'Guidance Counselor' },
    { value: 'aide', label: 'Paraprofessional Aide' }
  ];

  const communicationOptions = [
    { value: 'verbal', label: 'Verbal Communication' },
    { value: 'sign-language', label: 'Sign Language' },
    { value: 'picture-exchange', label: 'Picture Exchange System (PECS)' },
    { value: 'assistive-device', label: 'Assistive Communication Device' },
    { value: 'written', label: 'Written Communication' },
    { value: 'gestures', label: 'Gestures and Body Language' }
  ];

  // Hydrate the form from the student prop ONCE per student id — not on
  // every re-render of the parent. Without this guard, a StudentsContext
  // refresh (real-time subscription in live mode, storage-event in demo
  // mode) creates a new `student` object reference, this effect fires,
  // and every field the teacher had typed gets overwritten with the
  // server value. On mobile the practical effect was: type in a field,
  // wait a tick for the context to refresh, watch your text vanish.
  const lastHydratedIdRef = useRef(null);
  useEffect(() => {
    if (!student) return;
    if (lastHydratedIdRef.current === student.id) return;
    lastHydratedIdRef.current = student.id;
    setFormData({
      firstName: student?.firstName || '',
      lastName: student?.lastName || '',
      dateOfBirth: student?.dateOfBirth || '',
      grade: student?.grade || '',
      studentId: student?.studentId || '',
      primaryDisability: student?.primaryDisability || '',
      secondaryDisabilities: student?.secondaryDisabilities || [],
      accommodations: student?.accommodations || '',
      serviceProviders: student?.serviceProviders || [],
      emergencyContact: student?.emergencyContact || {
        name: '',
        relationship: '',
        phone: '',
        email: ''
      },
      medicalInfo: student?.medicalInfo || '',
      behavioralNotes: student?.behavioralNotes || '',
      academicLevel: student?.academicLevel || '',
      communicationMethod: student?.communicationMethod || '',
      transportationNeeds: student?.transportationNeeds || '',
      dietaryRestrictions: student?.dietaryRestrictions || '',
      iepStartDate: student?.iepStartDate || '',
      iepAnnualReviewDue: student?.iepAnnualReviewDue || ''
    });
  }, [student]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasUnsavedChanges(true);
    
    // Clear error for this field
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleNestedInputChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev?.[parent],
        [field]: value
      }
    }));
    setHasUnsavedChanges(true);

    // Clear the error for this nested field. Without this, "Emergency
    // contact phone is required" stays lit up even after the teacher
    // types a valid phone number — because handleInputChange's error-
    // clearing branch (below) keys off flat field names like
    // 'firstName', but errors[] uses dotted keys like
    // 'emergencyContact.phone' for nested fields.
    const errorKey = `${parent}.${field}`;
    if (errors?.[errorKey]) {
      setErrors(prev => ({
        ...prev,
        [errorKey]: null
      }));
    }
  };

  // Only firstName + lastName are truly required at edit time. Everything
  // else on the profile is optional — a student can join the roster from
  // the quick-add modal (which only requires first/last/grade) and get
  // their IEP paperwork filled in later. The previous version required
  // dateOfBirth / studentId / primaryDisability / emergencyContact.name
  // + phone here, which locked teachers into an unsavable form for any
  // student added via the quick-add path.
  //
  // Grade + primaryDisability could be argued either way — leaving them
  // optional at edit time so teachers can incrementally fill in a
  // profile without hitting a validation wall. The add-student modal
  // has its own stricter rules for the create path.
  const validateForm = () => {
    const newErrors = {};

    if (!formData?.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData?.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Real save: hand the form data to the parent's onSave, which pipes
    // it through StudentsContext.updateStudent (Supabase in live mode,
    // localStorage in demo mode). All new fields from migration 0010 —
    // emergency contact, accommodations, secondary disabilities, service
    // providers, medical info, behavioral notes, academic level,
    // communication method, transportation and dietary needs — are now
    // in the patch that context turns into a real UPDATE.
    try {
      await onSave?.(formData);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving student profile:', error);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
        onCancel?.();
        setHasUnsavedChanges(false);
      }
    } else {
      onCancel?.();
    }
  };

  return (
    // noValidate so the browser's native "Please fill out this field"
    // tooltip on a `required` <input type="date"> etc. doesn't intercept
    // the submit before our validateForm() runs. All required-field UI
    // is rendered by the form components themselves via the `errors[]`
    // state above.
    <form onSubmit={handleSubmit} className="space-y-8" noValidate>
      {/* Basic Information */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-semibold text-card-foreground mb-4 flex items-center gap-2">
          <Icon name="User" size={20} />
          Basic Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="First Name"
            type="text"
            value={formData?.firstName}
            onChange={(e) => handleInputChange('firstName', e?.target?.value)}
            error={errors?.firstName}
            required
            placeholder="Enter first name"
          />
          
          <Input
            label="Last Name"
            type="text"
            value={formData?.lastName}
            onChange={(e) => handleInputChange('lastName', e?.target?.value)}
            error={errors?.lastName}
            required
            placeholder="Enter last name"
          />
          
          <Input
            label="Date of Birth"
            type="date"
            value={formData?.dateOfBirth}
            onChange={(e) => handleInputChange('dateOfBirth', e?.target?.value)}
            error={errors?.dateOfBirth}
          />

          <Select
            label="Grade Level"
            options={gradeOptions}
            value={formData?.grade}
            onChange={(value) => handleInputChange('grade', value)}
            error={errors?.grade}
            placeholder="Select grade level"
          />

          <Input
            label="Student ID"
            type="text"
            value={formData?.studentId}
            onChange={(e) => handleInputChange('studentId', e?.target?.value)}
            error={errors?.studentId}
            placeholder="Enter student ID"
          />
        </div>
      </div>
      {/* IEP Timeline */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-semibold text-card-foreground mb-4 flex items-center gap-2">
          <Icon name="CalendarClock" size={20} />
          IEP Timeline
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="IEP start date"
            type="date"
            value={formData?.iepStartDate}
            onChange={(e) => {
              const start = e?.target?.value || '';
              handleInputChange('iepStartDate', start);
              if (start && !formData?.iepAnnualReviewDue) {
                const d = new Date(start);
                if (!Number.isNaN(d.getTime())) {
                  d.setFullYear(d.getFullYear() + 1);
                  const yyyy = d.getFullYear();
                  const mm = String(d.getMonth() + 1).padStart(2, '0');
                  const dd = String(d.getDate()).padStart(2, '0');
                  handleInputChange(
                    'iepAnnualReviewDue',
                    `${yyyy}-${mm}-${dd}`,
                  );
                }
              }
            }}
            description="When the current IEP took effect."
          />
          <Input
            label="Annual review due"
            type="date"
            value={formData?.iepAnnualReviewDue}
            onChange={(e) =>
              handleInputChange('iepAnnualReviewDue', e?.target?.value || '')
            }
            description="Federal 12-month deadline. Auto-fills to +1 year when you set a start date."
          />
        </div>
      </div>
      {/* Disability Information */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-semibold text-card-foreground mb-4 flex items-center gap-2">
          <Icon name="Heart" size={20} />
          Disability Information
        </h3>
        
        <div className="space-y-4">
          <Select
            label="Primary Disability"
            options={disabilityOptions}
            value={formData?.primaryDisability}
            onChange={(value) => handleInputChange('primaryDisability', value)}
            error={errors?.primaryDisability}
            placeholder="Select primary disability"
          />
          
          <Select
            label="Secondary Disabilities"
            options={disabilityOptions}
            value={formData?.secondaryDisabilities}
            onChange={(value) => handleInputChange('secondaryDisabilities', value)}
            multiple
            placeholder="Select secondary disabilities (optional)"
            description="Select any additional disabilities that apply"
          />
          
          <div>
            <label
              htmlFor="student-accommodations"
              className="mb-1 block text-sm font-medium text-foreground"
            >
              Accommodations &amp; Modifications
            </label>
            <textarea
              id="student-accommodations"
              value={formData?.accommodations}
              onChange={(e) => handleInputChange('accommodations', e?.target?.value)}
              rows={4}
              placeholder={"Extended time on tests\nPreferred seating\nBreaks every 20 minutes\n…"}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              One accommodation per line. Everything here saves to the
              student's record and appears on the printable one-pager
              you take to parent meetings.
            </p>
          </div>
        </div>
      </div>
      {/* Service Provider disciplines (informational).
          The real team assignments — actual named people who can log
          data for this student — live in the "Team on this student"
          section of the profile Overview. That section reads from
          public.student_team_members. This dropdown just captures
          which disciplines are on the IEP, for teachers whose team
          hasn't been fully invited yet. */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-semibold text-card-foreground mb-4 flex items-center gap-2">
          <Icon name="Users" size={20} />
          Service Provider disciplines
        </h3>

        <Select
          label="Disciplines on this IEP"
          options={serviceProviderOptions}
          value={formData?.serviceProviders}
          onChange={(value) => handleInputChange('serviceProviders', value)}
          multiple
          searchable
          placeholder="Which specialties should this student be seeing?"
          description="Names + credentials of the actual staff — SLP Jamie R., OT Aisha P., etc. — are managed under Team on this student on the profile view."
        />
      </div>
      {/* Emergency Contact */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-semibold text-card-foreground mb-4 flex items-center gap-2">
          <Icon name="Phone" size={20} />
          Emergency Contact
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Contact Name"
            type="text"
            value={formData?.emergencyContact?.name}
            onChange={(e) => handleNestedInputChange('emergencyContact', 'name', e?.target?.value)}
            error={errors?.['emergencyContact.name']}
            placeholder="Enter contact name"
          />

          <Input
            label="Relationship"
            type="text"
            value={formData?.emergencyContact?.relationship}
            onChange={(e) => handleNestedInputChange('emergencyContact', 'relationship', e?.target?.value)}
            placeholder="e.g., Parent, Guardian"
          />

          <Input
            label="Phone Number"
            type="tel"
            value={formData?.emergencyContact?.phone}
            onChange={(e) => handleNestedInputChange('emergencyContact', 'phone', e?.target?.value)}
            error={errors?.['emergencyContact.phone']}
            placeholder="Enter phone number"
          />
          
          <Input
            label="Email Address"
            type="email"
            value={formData?.emergencyContact?.email}
            onChange={(e) => handleNestedInputChange('emergencyContact', 'email', e?.target?.value)}
            placeholder="Enter email address"
          />
        </div>
      </div>
      {/* Additional Information */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-semibold text-card-foreground mb-4 flex items-center gap-2">
          <Icon name="FileText" size={20} />
          Additional Information
        </h3>
        
        <div className="space-y-4">
          <Select
            label="Communication Method"
            options={communicationOptions}
            value={formData?.communicationMethod}
            onChange={(value) => handleInputChange('communicationMethod', value)}
            placeholder="Select primary communication method"
          />
          
          <Input
            label="Academic Level"
            type="text"
            value={formData?.academicLevel}
            onChange={(e) => handleInputChange('academicLevel', e?.target?.value)}
            placeholder="Describe current academic functioning level"
          />
          
          <Input
            label="Medical Information"
            type="text"
            value={formData?.medicalInfo}
            onChange={(e) => handleInputChange('medicalInfo', e?.target?.value)}
            placeholder="Any relevant medical conditions or medications"
          />
          
          <Input
            label="Behavioral Notes"
            type="text"
            value={formData?.behavioralNotes}
            onChange={(e) => handleInputChange('behavioralNotes', e?.target?.value)}
            placeholder="Important behavioral considerations or strategies"
          />
          
          <Input
            label="Transportation Needs"
            type="text"
            value={formData?.transportationNeeds}
            onChange={(e) => handleInputChange('transportationNeeds', e?.target?.value)}
            placeholder="Special transportation requirements"
          />
          
          <Input
            label="Dietary Restrictions"
            type="text"
            value={formData?.dietaryRestrictions}
            onChange={(e) => handleInputChange('dietaryRestrictions', e?.target?.value)}
            placeholder="Any food allergies or dietary needs"
          />
        </div>
      </div>
      {/* Form Actions */}
      <div className="flex items-center justify-between pt-6 border-t border-border">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {hasUnsavedChanges && (
            <>
              <Icon name="AlertCircle" size={16} className="text-warning" />
              <span>You have unsaved changes</span>
            </>
          )}
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </Button>
          
          <Button
            type="submit"
            loading={isSaving}
            iconName="Save"
            iconPosition="left"
          >
            {isSaving ? 'Saving...' : 'Save Profile'}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default StudentProfileForm;